import { ethers } from 'ethers';
import { useCallback, useEffect, useMemo, useState } from 'react';
import BigNumber from 'bignumber.js';
import { useDispatch, useSelector } from 'react-redux';
import { APPROVE_ERC20_GAS_LIMIT, TxStatus } from '@orionprotocol/sdk';
import { i18n } from '@lingui/core';
import { useERC20Contract } from '@orionprotocol/utils';

 
import { getWalletAddress } from '../../redux/selectors';
import { useAddTxToQueue } from '../../redux/actionCreators';
import { transactionsSlice } from '../../redux/slices';
import { displayError } from '../../utils';
import { useProvider } from '../providers';

import { useTokenDecimals } from './useTokenDecimals';

const AddressZero = ethers.ZeroAddress;

type TokenAllowanceService = {
  needReset: boolean;
  allowance: BigNumber | undefined;
  reset: () => void;
  approve: (
    amount: ethers.BigNumberish,
    onTxSuccess?: ({
      txId,
      txReceipt,
    }: {
      txId: string;
      txReceipt: ethers.TransactionReceipt;
    }) => void,
    onTxFail?: ({ txId, status }: { txId: string; status?: TxStatus }) => void,
  ) => void;
  approving: boolean;
  resetting: boolean;
  approveInfinite: (onSuccess?: () => void, onFail?: () => void) => void;
};

export const useTokenAllowance = (
  tokenAddress: string | undefined,
  spender: string | undefined,
): TokenAllowanceService => {
  const isNativeCurrency = useMemo(
    () => tokenAddress === AddressZero,
    [tokenAddress],
  );
  const provider = useProvider();
  const contract = useERC20Contract(tokenAddress, provider);
  const walletAddress = useSelector(getWalletAddress);
  const owner = useSelector(getWalletAddress);
  const [needReset, setNeedReset] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [allowance, setAllowance] = useState<BigNumber>();
  const [active, setActive] = useState(false);
  const dispatch = useDispatch();
  const addTxToQueue = useAddTxToQueue();

  useEffect(() => {
    if (!active) setResetting(false);
  }, [active]);

  useEffect(() => {
    if (isNativeCurrency) setAllowance(new BigNumber(Infinity));
  }, [isNativeCurrency]);

  const decimals = useTokenDecimals(tokenAddress, provider);
  const updateAllowance = useCallback(() => {
    if (
      decimals !== undefined &&
      contract &&
      owner !== undefined &&
      spender !== undefined
    ) {
      contract
        .allowance(owner, spender)
        .then((data) => {
          const result = new BigNumber(data.toString());
          const precision = new BigNumber(10).pow(decimals.toString());
          const allowanceNormalized = result.div(precision);
          setAllowance(new BigNumber(allowanceNormalized.toString()));
        })
        .catch(displayError(i18n._("Couldn't get allowance")));
    }
  }, [contract, decimals, owner, spender]);

  const checkNeedZeroReset = useCallback(async () => {
    if (
      contract &&
      spender !== undefined &&
      provider &&
      walletAddress !== undefined
    ) {
      const unsignedTx = await contract.approve.populateTransaction(
        spender,
        ethers.MaxUint256,
      );
      unsignedTx.from = walletAddress;
      try {
        await provider.estimateGas(unsignedTx);
        return false;
      } catch (e) {
        return true;
      }
    }
    return false;
  }, [contract, provider, spender, walletAddress]);

  const approve = useCallback(
    (
      amount: ethers.BigNumberish,
      onTxSuccess?: ({
        txId,
        txReceipt,
      }: {
        txId: string;
        txReceipt: ethers.TransactionReceipt;
      }) => void,
      onTxFail?: ({
        txId,
        status,
      }: {
        txId: string;
        status?: TxStatus;
      }) => void,
    ) => {
      if (isNativeCurrency)
        throw new Error(i18n._('No need approve for native currency'));
      if (contract && spender !== undefined && walletAddress !== undefined) {
        contract.approve
          .populateTransaction(spender, amount)
          .then((unsignedTx) => {
            setActive(true);

            addTxToQueue({
              unsignedTx,
              summary: i18n._('Approve'),
              gasLimit: APPROVE_ERC20_GAS_LIMIT,
              onTxIncludedInChain: () => setActive(false),
              onTxError: () => {
                setActive(false);
                dispatch(transactionsSlice.actions.removeLastSignFailedTx());
              },
              onTxFail: ({ txId, status }) => {
                setActive(false);
                dispatch(transactionsSlice.actions.removeLastSignFailedTx());
                if (onTxFail) {
                  onTxFail({ txId, status });
                }
              },
              onTxSuccess,
            });
          })
          .catch(displayError(i18n._("Couldn't approve")));
        checkNeedZeroReset()
          .then(setNeedReset)
          .catch(displayError(i18n._("Couldn't check need zero reset")));
      }
    },
    [
      addTxToQueue,
      checkNeedZeroReset,
      contract,
      dispatch,
      isNativeCurrency,
      spender,
      walletAddress,
    ],
  );

  useEffect(() => {
    let interval: NodeJS.Timer;
    if (!isNativeCurrency) {
      checkNeedZeroReset()
        .then(setNeedReset)
        .catch(() => setNeedReset(true));
      interval = setInterval(() => {
        checkNeedZeroReset()
          .then(setNeedReset)
          .catch(() => setNeedReset(true));
      }, 5000);
    }
    return (): void => {
      clearInterval(interval);
    };
  }, [checkNeedZeroReset, isNativeCurrency]);

  useEffect(() => {
    updateAllowance();
    let interval: NodeJS.Timer;
    if (!isNativeCurrency) {
      updateAllowance();
      interval = setInterval(updateAllowance, 3000);
    }
    return (): void => {
      clearInterval(interval);
    };
  }, [active, isNativeCurrency, updateAllowance]);

  const approveInfinite = (onSuccess?: () => void, onFail?: () => void): void =>
    approve(ethers.MaxUint256, onSuccess, onFail);
  const reset = (): void => {
    setResetting(true);
    approve(0);
  };

  return {
    needReset,
    allowance,
    reset,
    approve,
    approving: active,
    resetting,
    approveInfinite,
  };
};
